import RootStore from '@App/stores/root-store'
import { SongwriterSummary } from '@Entities/songwriter/songwriter-summary'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { PaginationBody, PaginationBodyRightsHolders, PaginationInfo } from '@Entities/pagination'
import { AxiosResponse } from 'axios'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import { getSongwriters } from '@App/api/routes/songwriter/songwriter'
import { ResultPage } from '@Entities/result-page'

class SongwriterSearchStore {
    private rootStore: RootStore

    private _songwriters: ResultPage<SongwriterSummary>

    private _paginationInfo: PaginationInfo

    private _currentSearchValue = ''

    constructor(rootStore: RootStore) {
        makeObservable<SongwriterSearchStore, '_songwriters' | '_paginationInfo' | '_currentSearchValue'>(this, {
            _songwriters: observable,
            _paginationInfo: observable,
            _currentSearchValue: observable,
            setCurrentSearchValue: action,
            songwriterCount: computed,
        })
        this.rootStore = rootStore
    }

    get songwriters() {
        return this._songwriters
    }

    get paginationInfo() {
        return this._paginationInfo
    }

    get currentSearchValue() {
        return this._currentSearchValue
    }

    get songwriterCount(): number {
        if (!this._songwriters) {
            return 0
        }
        return this._songwriters.totalElements
    }

    setCurrentSearchValue(value: string) {
        this._currentSearchValue = value
    }

    public loadSongwriters = async (paginationInfo: PaginationBody, search?: string) => {
        this.setCurrentSearchValue(search ?? '')
        await this.setSongwritersAndPagination(paginationInfo, (passedPagInfo: PaginationBody) =>
            getSongwriters(passedPagInfo, search)
        )
    }

    private setSongwritersAndPagination = async (
        paginationInfo: PaginationBody,
        fetchFn: (paginationInfo: PaginationBodyRightsHolders) => Promise<AxiosResponse<ResultPage<SongwriterSummary>>>
    ) => {
        const songwriters = await fetchFn(paginationInfo)
        runInAction(() => {
            this._songwriters = songwriters.data
            if (paginationInfo.limit && paginationInfo.page && songwriters) {
                this._paginationInfo = calcNumPages(
                    songwriters.data.totalElements,
                    paginationInfo.limit,
                    paginationInfo.page
                )
            }
        })
    }
}

export default SongwriterSearchStore
